import React, { useCallback, useEffect, useState } from "react";
import { GlobalApplicationState } from "globalApplicationState";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { push } from "react-router-redux";
import { Editor as IEditor} from "tinymce";
import { AsyncThunkAction} from "@reduxjs/toolkit";
import moment from "moment";

import AuthoringAppBar from "modules/common/components/authoring/authoringAppBar";
import EditorWrapperLayout from "modules/common/components/authoring/editorLayouts/editorWrapperLayout";
import MainEditorLayout from "modules/common/components/authoring/editorLayouts/mainEditorLayout";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import { PickerLocalization } from "modules/common/components/pickerLocalization";
import BasePage from "pages/common/basePage";
import { actions as AudienceActions } from "modules/audiences";
import SmsSettingsPanel from "./smsSettingsPanel";
import { BackItem } from "pages/common/breadcrumb";
import PreviewPhone from "./previewPhone";
import SmsAuthoringActions from "./smsAuthoringActions";
import { ISMS, ISMSPreviewDetails } from "./models";
import { createNewSMS, DEFAULT_SMS, getSMSToEdit, ICreateNewSMSThunkPayload, saveExistingSMS, smsSlice } from "./reducer";
import { IdNamePair } from "modules/common/models";
import ShallowEquals from "utils/shallowEquals";
import { htmlIsEqual } from "utils/htmlIsEqual";
import Loading from "modules/common/components/loading";
import ConfirmDialog from "modules/common/components/dialogs/confirmDialog";
import SMSOneLastCheck from "./smsOneLastCheck";
import { smsApi } from "api/instances";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { useOpenContentGuidelines } from "modules/common/hooks/useOpenContentGuidelines";
import ContentGuidelinePreview from "modules/settings/components/content/guidelines/contentGuidelinePreview";
import { HintType } from "modules/common/components/authoring/dialogs/guidelineHint";
import { useDisplayNav } from "modules/common/hooks/useDisplayNav";
import { BACK_SMS_TEXT_FULL } from "modules/common/components/authoring/constants";

import "../common/components/authoring/styles/authoringLayout.sass";
import "./styles/smsEditor.sass";

interface ISmsEditorPageProps {};

const SmsEditorPage: React.FC<IConnectedSmsEditorPageProps> = ({
    getAudiences,
    redirectTo,
    tenantId,
    sms,
    editor,
    changedSinceSaved,
    shouldOpenOneLastCheck,
    match,
    isFetching,
    isSaving
}) => {
    const smsId = match.params.smsId;
    const editorSMSId = sms.id;
    const manageSMSUrl = `/${tenantId}/admin/sms`;

    const [settingsDrawerOpen, setSettingsDrawerOpen] = useState<boolean>(true);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [isSendingSMS, setIsSendingSMS] = useState(false);
    const [previewText, setPreviewText] = React.useState(sms.plainBody);
    const [showOneLastCheck, setShowOneLastCheck] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);

    const [audienceUsers, setAudienceUsers] = React.useState<number>(0);
    const [totalUsers, setTotalUsers] = React.useState<number>(0);
    
    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();
    const { openContentGuidelines, setOpenContentGuidelines } = useOpenContentGuidelines();
    useDisplayNav(false, { backgroundColor: "#FFFFFF" });
    
    const isLoading = isFetching;
    const isSMSIdPresent = React.useMemo(() => !!smsId && smsId !== "new", [smsId]);
    const isSMSValid = sms.title.length > 0 && sms.htmlBody.length > 0 && sms.plainBody.length > 0 && (sms.audiences.length > 0 || sms.sendToAllUsers);
    const editorRef = React.useRef<IEditor | null>(null);

    const dispatch = useDispatch();

    const setShouldOpenOneLastCheck = useCallback((shouldOpen: boolean) => 
        dispatch({ type: smsSlice.actions.SET_SHOULD_OPEN_ONE_LAST_CHECK, payload: { shouldOpen }}), [dispatch]);

    const showSMSSentConfirmation = (sentTime: string, smsId: string) => dispatch({ 
        type: smsSlice.actions.SET_SHOW_SENT_CONFIRMATION, 
        payload: { 
            sentConfirmation: {
                shouldShowDialog: true,
                expectedSentTime: sentTime,
                smsId
            }
        }
    });

    //Fetch audiences on page load.
    useEffect(() => {
        getAudiences();
    }, [getAudiences]);

    //Update preview text whenever body is updated.
    useEffect(() => {
        setPreviewText(sms.plainBody);
    }, [sms.plainBody]);

    const onToggleDrawer = useCallback(() => {
        setSettingsDrawerOpen(!settingsDrawerOpen);
    }, [setSettingsDrawerOpen, settingsDrawerOpen]);

    const onSendSMS = async () => {
        setIsSendingSMS(true);

        try {
            await smsApi.sendSMS(editorSMSId);
            showSMSSentConfirmation(
                sms.sentTime && new Date(sms.sentTime) >= new Date() 
                    ? sms.sentTime
                    : new Date().toISOString(),
                editorSMSId
            );
            redirectToManageSMS();
        }
        catch {
            setErrorMessage("There was an error. Please try again.");
            setIsSendingSMS(false);
            setShowOneLastCheck(false);
        }
    }
    
    const saveSMS = async (redirectUrl?: string, sendNotification: boolean = true) => {
        if (isLoading) return;
        let callResults: AsyncThunkAction<void, ICreateNewSMSThunkPayload, {}>;

        //It claims await is unnecessary but it does affect the result.
        isSMSIdPresent 
            ? callResults = await dispatch(saveExistingSMS({ redirectUrl }))
            : callResults = await dispatch(createNewSMS({ redirectUrl }));

        if(sendNotification) {
            if(callResults["type"].includes("fulfilled")) {
                setSuccessMessage("Successfully saved as a draft.")
            }
            else {
                setErrorMessage("Something went wrong. Please try again later.")
            }
        }
    };

    const onClickSend = async () => {
        if (!editorSMSId || changedSinceSaved) {
            await saveSMS();
            setShouldOpenOneLastCheck(true);
        }
        else {
            setShowOneLastCheck(true);
        }
    };

    const redirectToManageSMS = () => {
        dispatch({
            type: smsSlice.actions.SET_SMS_EDITOR, 
            payload: { 
                sms: DEFAULT_SMS
            }
        });
        dispatch({type: smsSlice.actions.SET_TEST_SMS, payload: { testNumbers: [] }});
        redirectTo(manageSMSUrl)
    };

    const backToManageSMS = React.useCallback(() => {
        if(changedSinceSaved) {
            setShowUnsavedWarning(true)
        }
        else {
            redirectToManageSMS();
        }
    }, [changedSinceSaved, redirectToManageSMS]);

    const getActions = React.useMemo((): JSX.Element =>
        <SmsAuthoringActions
            onSend={onClickSend}
            isSendDisabled={!isSMSValid}
            onDiscard={() => changedSinceSaved && setShowDiscardDialog(true)}
            isDiscardDisabled={(!changedSinceSaved || !isSMSIdPresent)}
            onSave={async() => await saveSMS()}
            isSaveDisabled={!changedSinceSaved}
            onBack={backToManageSMS}
            onToggleSettingsPanel={onToggleDrawer}
            isToggleSettingsPanelDisabled={!settingsDrawerOpen}
            openGuidelines={() => setOpenContentGuidelines(true)}
        />, 
        [settingsDrawerOpen, changedSinceSaved, isSMSValid, backToManageSMS, isSMSIdPresent, onClickSend, onToggleDrawer, saveSMS, setOpenContentGuidelines]
    );

    const contentGuidelinePreview = React.useMemo(() => {
        return <ContentGuidelinePreview
            showPreview={openContentGuidelines}
            onClose={() => setOpenContentGuidelines(false)}
            isAuthoring
            hintType={HintType.Post}
        />;
    }, [openContentGuidelines, setOpenContentGuidelines]);
    
    const hasSMSUpdated = (updatedSMSFields: Partial<ISMS>): boolean => {
        const updatedSMS = { ...editor.sms, ...updatedSMSFields };
        const originalSMS = editor.originalSms;

        return !htmlIsEqual(updatedSMS.htmlBody, originalSMS.htmlBody) ||
            !ShallowEquals(updatedSMS.plainBody, originalSMS.plainBody) ||
            !ShallowEquals(updatedSMS.title, originalSMS.title) ||
            !audienceIsEqual(updatedSMS.audiences, originalSMS) || 
            !ShallowEquals(updatedSMS.sendToAllUsers, originalSMS.sendToAllUsers) ||
            !ShallowEquals(updatedSMS.fromSender, originalSMS.fromSender) ||
            !ShallowEquals(updatedSMS.fromNumber, originalSMS.fromNumber) ||
            !sentTimeIsEqual(updatedSMS.sentTime, originalSMS.sentTime);
    }

    const audienceIsEqual = (newAudience: IdNamePair<string, string>[], originalSMS: ISMS): boolean => {
        const newAudienceIds = newAudience.map(a => a.id);
        const originalAudienceIds = originalSMS.audiences.map(a => a.id);

        if (newAudienceIds.length !== originalAudienceIds.length)
            return false;

        const originalAudIdSet = new Set(originalAudienceIds.sort());

        return newAudienceIds.every(id => originalAudIdSet.has(id));
    }
    
    const sentTimeIsEqual = (newSentTimeString?: string, originalSentTime?: string): boolean => {
        return newSentTimeString === originalSentTime || 
            moment(newSentTimeString).isSame(moment(originalSentTime), "minutes");
    }

    const updateSMS = (updatedSMSFields: Partial<ISMS>, checkIfChanged: boolean = true) => {
        dispatch({ 
            type: smsSlice.actions.UPDATE_SMS_EDITOR, 
            payload: { 
                sms: {
                    ...updatedSMSFields
                } as Partial<ISMS>,
                changedSinceLastSaved: checkIfChanged ? hasSMSUpdated(updatedSMSFields) : undefined
            }
        });
    }

    //Redirect user after saving draft in case they're on a /new/ prior.
    useEffect(() => {
        if (editorSMSId !== "" && !isSMSIdPresent)
            redirectTo(`/${match.params.tenant}/admin/sms/edit/${editorSMSId}`);
    }, [editorSMSId, isSMSIdPresent, match.params.tenant, redirectTo]);

    //Fetch SMS if user is loading page with an id.
    useEffect(() => {
        if (isSMSIdPresent) {
            dispatch(getSMSToEdit({ smsId }));
        }
    }, [isSMSIdPresent, dispatch, smsId]);

    //Logic for showing the last check modal.
    useEffect(() => {
        if (editorSMSId && !changedSinceSaved) {
            if (shouldOpenOneLastCheck) {
                setShouldOpenOneLastCheck(false);
                setShowOneLastCheck(true);
            }
        }
    }, [editorSMSId, changedSinceSaved, shouldOpenOneLastCheck, setShouldOpenOneLastCheck]);

    const onClickMessageBox = () => {
        if (!settingsDrawerOpen) {
            setSettingsDrawerOpen(true);
        }

        if(editorRef && editorRef.current) {
            editorRef.current.focus();
    
            //Move caret to end
            editorRef.current.selection.select(editorRef.current.getBody(), true);
            editorRef.current.selection.collapse(false);
        }
    }

    const FetchSMSDetailsForLastCheck = (sms: ISMS): ISMSPreviewDetails => {
        return {
            title: sms.title,
            sentTime: sms.sentTime,
            sendToAllUsers: sms.sendToAllUsers,
            audiences: sms.audiences,
            textBody: sms.plainBody,
            fromNumber: sms.fromNumber!
        }
    }

    return ( isLoading 
        ? <Loading />
        : <BasePage fullWidth>
            <AuthoringAppBar
                backAction={
                    <BackItem 
                        textStyle={{ fontWeight: 500 }} 
                        title={BACK_SMS_TEXT_FULL}
                        onClick={backToManageSMS}
                    />
                }
                actions={getActions}
                />
            <div id="sms-editor-content" className="authoring-page">
                <LoadingOverlay show={isSaving} styles={{ zIndex: 1201 }} />
                <EditorWrapperLayout>
                    <MainEditorLayout
                        paperProps={{
                            className: `main-editor-paper sms-editor-paper main-editor-paper-${settingsDrawerOpen ? "open" : "closed"}`,
                            elevation: 0,
                        }}
                        >   
                        <PreviewPhone 
                            phoneNumber={sms.fromNumber ?? process.env.REACT_APP_DEFAULT_SMS_NUMBER!} 
                            previewTextMessage={previewText}
                            drawerOpenPadding={settingsDrawerOpen}
                            onClickMessageBox={() => onClickMessageBox()}
                            showDisclaimer
                        />
                    </MainEditorLayout>
                </EditorWrapperLayout>
                <PickerLocalization>
                    <SmsSettingsPanel
                        open={settingsDrawerOpen}
                        toggleOpen={onToggleDrawer}
                        updateSMS={updateSMS}
                        updatePreview={setPreviewText}
                        onSuccessMessage={setSuccessMessage}
                        onErrorMessage={setErrorMessage}
                        onSaveSMS={() => saveSMS(undefined, false)}
                        sms={sms}
                        totalAudienceUsers={totalUsers}
                        audienceUsers={audienceUsers}
                        setTotalUsers={setTotalUsers}
                        setAudienceUsers={setAudienceUsers}
                        disableEditor={isSaving || isFetching}
                        editorRef={editorRef}
                    />
                </PickerLocalization>
            </div>
            <ConfirmDialog
                title={BACK_SMS_TEXT_FULL}
                confirmLabel="KEEP EDITING"
                denyLabel="EXIT"
                open={showUnsavedWarning}
                onClose={() => setShowUnsavedWarning(false)}
                onConfirm={() => setShowUnsavedWarning(false)}
                onDeny={redirectToManageSMS}
            >
                <div style={{ minWidth: 400 }}>
                    <div>
                        You have unsaved changes to your message. Changes will not be saved if you exit.</div>
                    <br />
                    <div>Do you want to continue?</div>
                </div>
            </ConfirmDialog>
            <ConfirmDialog
                title="Discard Changes"
                confirmLabel="SAVE CHANGES"
                denyLabel="DISCARD"
                open={showDiscardDialog}
                onClose={() => setShowDiscardDialog(false)}
                onConfirm={async () => {
                    setShowDiscardDialog(false);
                    await saveSMS(manageSMSUrl);
                    redirectToManageSMS();
                }}
                onDeny={redirectToManageSMS}
            >
                <div style={{ minWidth: 400 }}>
                    You have unsaved changes to your draft. Would you like to save these changes before exiting?
                </div>
            </ConfirmDialog>
            {contentGuidelinePreview}
            <SMSOneLastCheck
                show={showOneLastCheck}
                onClose={() => setShowOneLastCheck(false)}
                sending={isSendingSMS}
                sendSMS={onSendSMS}
                totalAudienceUsers={totalUsers}
                audienceUsers={audienceUsers} 
                downloadOptedInUsers
                
                smsDetails={FetchSMSDetailsForLastCheck(sms)}
                hideSentTime={false}
            />
        </BasePage>
    );
};

interface RouteParams {
    tenant: string;
    smsId: string;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ISmsEditorPageProps & RouteComponentProps<RouteParams>) => ({
        ...ownProps,
        tenantId: state.tenant.id,
        sms: state.sms.editor.sms,
        editor: state.sms.editor,
        changedSinceSaved: state.sms.editor.changedSinceLastSaved,
        shouldOpenOneLastCheck: state.sms.editor.shouldOpenOneLastCheck,
        isFetching: state.sms.editor.isFetching,
        isSaving: state.sms.editor.isSaving,
    }),
    {
        redirectTo: push,
        getAudiences: AudienceActions.getAudiences,
    }
);
type IConnectedSmsEditorPageProps = ConnectedProps<typeof connector>;

export default withRouter(connector(SmsEditorPage));